/* @flow */

import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';

import customSort from 'lib/sortHelpers';
import LoadingComponent from 'components/loading';
import LoadingDialogComponent from 'components/loading/dialog';

import BillsPortfolioTableViewComponent from './view';
import BillsPortfolioTableControlsViewComponent from './tableControls';

import Bill from 'models/documents/bill';
import BillActiveFilters from 'models/documents/billActiveFilters';
import FileExportDialogComponent from 'components/fileExportDialog';

import DownloadSelectedBillsRequest from 'models/requests/documents/downloadSelectedBillsRequest';
import DocumentService from 'services/DocumentService';
import NotificationHelper from 'lib/notifications';

import { ROLE_SELLER } from 'constants/companyRoles';
import {
  BILL_TYPE_ATTESTATION,
  BILL_TYPE_MONTHLY_ATTESTATION
} from 'constants/constants';

type BillsPortfolioComponentProps = {
  activeCompanyId: number,
  activeCompanyRole: number
};

type BillsPortfolioComponentState = {
  activeFilters: BillActiveFilters,
  bills: Bill[],
  isLoading: boolean,
  isProcessing: boolean,
  order: string,
  orderBy: string | null,
  page: number,
  pageSize: number,
  exportDialogOpen: boolean,
  exportContext: { mode: null | 'single' | 'bulk', billId: null | number }
};

class BillsPortfolioComponent extends React.Component<BillsPortfolioComponentProps, BillsPortfolioComponentState> {
  constructor(props) {
    super(props);

    this.state = {
      activeFilters: new BillActiveFilters(),
      bills: [],
      isLoading: true,
      isProcessing: false,
      order: 'asc',
      orderBy: null,
      page: 0,
      pageSize: 10,
      exportDialogOpen: false,
      exportContext: { mode: null, billId: null } // 'single' | 'bulk'
    };
  }

  // ---------- helpers ----------
  getTypeId = (b: any): number =>
    (b && (b.typeId != null ? b.typeId : b.TypeId)) || 0;

  isAttestation = (b: any): boolean => {
    const typeId = this.getTypeId(b);
    return typeId === BILL_TYPE_ATTESTATION || typeId === BILL_TYPE_MONTHLY_ATTESTATION;
  };

  // accept either "hasXml" (camel) or "HasXml" (DTO) and never allow XML for attestations
  billHasXml = (b: any): boolean => {
    if (!b) return false;
    if (this.isAttestation(b)) return false;
    const flag = (b.hasXml != null ? b.hasXml : b.HasXml);
    return !!flag;
  };

  getSelectedBills = (): Bill[] => this.state.bills.filter(b => b.isSelected);
  getBillById = (id: number): ?Bill => this.state.bills.find(b => b.id === id);

  // ---------- dialog open/close ----------
  openExportDialogForSelected = async () => {
    if (this.state.isProcessing) return;
    const selected = this.getSelectedBills();
    if (selected.length === 0) return;

    const anyWithXml = selected.some(this.billHasXml);
    const allWithoutXml = selected.every(b => !this.billHasXml(b));

    // Rule: if every selected item lacks XML (including attestations), skip popup → PDFs
    if (allWithoutXml) {
      try {
        this.setState({ isProcessing: true, exportDialogOpen: false });
        await this.performDownloadSelectedBills('pdf');
      } catch (e) {
        console.error(e);
        NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
      } finally {
        this.setState({ isProcessing: false });
      }
      return;
    }

    // Mixed / some have XML → open dialog
    this.setState({ exportDialogOpen: true, exportContext: { mode: 'bulk', billId: null } });
  };

  openExportDialogForBill = async (billId: number) => {
    if (this.state.isProcessing) return;
    const bill = this.getBillById(billId);
    if (!bill) return;

    // Single: if bill has no XML (or is attestation), skip popup → PDF
    if (!this.billHasXml(bill)) {
      try {
        this.setState({ isProcessing: true, exportDialogOpen: false });
        await this.performDownloadBill(billId, 'pdf');
      } catch (e) {
        console.error(e);
        NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
      } finally {
        this.setState({ isProcessing: false });
      }
      return;
    }

    // Has XML → let user choose
    this.setState({ exportDialogOpen: true, exportContext: { mode: 'single', billId } });
  };

  closeExportDialog = () =>
    this.setState({ exportDialogOpen: false, exportContext: { mode: null, billId: null } });

  // ---------- export handlers ----------
  handleExport = async (format: 'pdf' | 'xml') => {
    if (this.state.isProcessing) return; // prevent double trigger
    const { mode, billId } = this.state.exportContext;

    // Close the modal immediately so the loading dialog is visible
    this.setState({ isProcessing: true, exportDialogOpen: false });

    try {
      if (mode === 'single' && billId != null) {
        await this.performDownloadBill(billId, format);
      }
      if (mode === 'bulk') {
        await this.performDownloadSelectedBills(format);
      }
    } catch (e) {
      console.error(e);
      NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
    } finally {
      this.setState({ isProcessing: false, exportContext: { mode: null, billId: null } });
    }
  };

  performDownloadBill = async (billId: number, format: 'pdf' | 'xml') => {
    const bill = this.getBillById(billId);
    const hasXml = this.billHasXml(bill);

    // Enforce: if XML picked but bill has no XML, fallback to PDF
    const finalFormat = format === 'xml' && !hasXml ? 'pdf' : format;

    if (finalFormat === 'pdf') {
      await DocumentService.downloadBill(this.props.activeCompanyId, billId);
    } else {
      await DocumentService.downloadBillXml(this.props.activeCompanyId, billId);
    }
  };

  performDownloadSelectedBills = async (format: 'pdf' | 'xml') => {
    const selected = this.getSelectedBills();
    if (selected.length === 0) return;

    if (format === 'pdf') {
      // PDFs for all selected
      const allIds = selected.map(b => b.id);
      const request = new DownloadSelectedBillsRequest(this.props.activeCompanyId, allIds);
      await DocumentService.downloadSelectedBills(request);
      this.unselectDownloadedBills(allIds);
      return;
    }

    // XML → only those with XML (and not attestations)
    const withXmlIds = selected.filter(this.billHasXml).map(b => b.id);

    if (withXmlIds.length === 0) {
      // No XML available for any selected: fallback to PDFs for all
      const allIds = selected.map(b => b.id);
      const requestPdf = new DownloadSelectedBillsRequest(this.props.activeCompanyId, allIds);
      await DocumentService.downloadSelectedBills(requestPdf);
      this.unselectDownloadedBills(allIds);
      return;
    }

    const requestXml = new DownloadSelectedBillsRequest(this.props.activeCompanyId, withXmlIds);
    await DocumentService.downloadSelectedBillsXml(requestXml);
    this.unselectDownloadedBills(withXmlIds);
  };

  // ---------- lifecycle ----------
  componentDidMount = async () => {
    try {
      const bills = await DocumentService.getBills(this.props.activeCompanyId);
      bills.forEach(b => (b.isSelected = false));
      this.setState({ bills, isLoading: false });
    } catch (e) {
      console.error(e);
    }
  };

  // ---------- table controls ----------
  handleFiltersChange = (activeFilters: BillActiveFilters) => this.setState({ activeFilters });

  handlePageChange = (page: number) => this.setState({ page });
  handlePageSizeChange = (pageSize: number) => this.setState({ pageSize });

  handleSortOrderChange = (orderBy: string) => {
    let order = this.state.orderBy === orderBy && this.state.order === 'desc' ? 'asc' : 'desc';

    const bills =
      order === 'desc'
        ? this.state.bills.sort((a, b) => customSort(b[orderBy], a[orderBy]))
        : this.state.bills.sort((a, b) => customSort(a[orderBy], b[orderBy]));

    this.setState({ bills, order, orderBy });
  };

  getFilteredResults = () => {
    let filteredBills = this.state.bills;

    // filter on query search
    if (this.state.activeFilters.query) {
      filteredBills = filteredBills.filter((b: Bill) => {
        const query = this.state.activeFilters.query.toUpperCase();
        let fields = [b.reference, b.amount].map(v => (v || '').toString().toUpperCase());
        if (this.props.activeCompanyRole == ROLE_SELLER) {
          fields = [b.reference, b.amount, b.invoiceReference].map(v => (v || '').toString().toUpperCase());
        }

        if (this.state.activeFilters.query.length > 0) {
          // Search an occurrence of the string
          return !!(fields.filter(field => field.toString().indexOf(query) !== -1)).length;
        } else {
          // Search the whole string
          return !fields.includes(query);
        }
      });
    }

    if (this.state.activeFilters.dateFrom !== null && this.state.activeFilters.dateTo !== null) {
      filteredBills = filteredBills.filter(b =>
        moment(b.issueDate).isBetween(this.state.activeFilters.dateFrom, this.state.activeFilters.dateTo, 'day', '[]')
      );
    }

    return filteredBills;
  };

  // ---------- selection helpers ----------
  selectBill = (billId: number) => {
    if (this.state.isProcessing) return;
    const bills = this.state.bills;
    const bill = bills.find(b => b.id === billId);
    if (bill) {
      bill.isSelected = !bill.isSelected;
      this.setState({ bills });
    }
  };

  selectPageBills = () => {
    if (this.state.isProcessing) return;
    const bills = this.state.bills;
    const filteredBills = this.getFilteredResults();
    const slice = filteredBills.slice(
      this.state.page * this.state.pageSize,
      this.state.page * this.state.pageSize + this.state.pageSize
    );
    const selectMode =
      bills.find(i => i.isSelected && slice.find(s => s.id === i.id) !== undefined) === undefined;
    bills.forEach(i => {
      if (slice.find(s => s.id === i.id) !== undefined) i.isSelected = selectMode;
    });
    this.setState({ bills });
  };

  unselectDownloadedBills = (bills: any[]) => {
    bills.forEach(billId => this.selectBill(billId));
  };

  downloadSelectedBills = async () => {
    if (this.state.isProcessing) return;
    const billIds = this.state.bills.filter(b => b.isSelected).map(b => b.id);
    try {
      this.setState({ isProcessing: true, exportDialogOpen: false });
      const request = new DownloadSelectedBillsRequest(this.props.activeCompanyId, billIds);
      await DocumentService.downloadSelectedBills(request);
      this.unselectDownloadedBills(billIds);
    } catch (e) {
      console.error(e);
      NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
    } finally {
      this.setState({ isProcessing: false });
    }
  };

  downloadBill = async (billId: number) => {
    if (this.state.isProcessing) return;
    try {
      this.setState({ isProcessing: true, exportDialogOpen: false });
      await DocumentService.downloadBill(this.props.activeCompanyId, billId);
    } catch (e) {
      console.error(e);
      NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
    } finally {
      this.setState({ isProcessing: false });
    }
  };

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    // Decide available formats for the dialog:
    // - Single: if bill has no XML (or attestation) -> ['pdf'] (but we actually skip popup).
    // - Bulk:
    //    - if at least one selected has XML -> ['pdf','xml']
    //    - if none have XML -> ['pdf'] (but we actually skip popup).
    let typesAllowed = ['pdf', 'xml'];
    const { mode, billId } = this.state.exportContext;

    if (mode === 'single' && billId != null) {
      const bill = this.getBillById(billId);
      if (!this.billHasXml(bill)) {
        typesAllowed = ['pdf'];
      }
    } else if (mode === 'bulk') {
      const selected = this.getSelectedBills();
      if (selected.length > 0 && selected.every(b => !this.billHasXml(b))) {
        typesAllowed = ['pdf'];
      }
    }

    return (
      <React.Fragment>
        <BillsPortfolioTableControlsViewComponent
          activeFilters={this.state.activeFilters}
          handleFiltersChange={this.handleFiltersChange}
        />

        <BillsPortfolioTableViewComponent
          bills={this.getFilteredResults()}
          order={this.state.order}
          orderBy={this.state.orderBy}
          page={this.state.page}
          pageSize={this.state.pageSize}
          downloadSelectedBills={this.openExportDialogForSelected}
          downloadBill={this.openExportDialogForBill}
          handlePageChange={this.handlePageChange}
          handlePageSizeChange={this.handlePageSizeChange}
          handleSortOrderChange={this.handleSortOrderChange}
          selectBill={this.selectBill}
          selectPageBills={this.selectPageBills}
        />

        {this.state.exportDialogOpen && (
          <FileExportDialogComponent
            isOpen
            typesAllowed={typesAllowed}
            handleExport={this.handleExport}
            handleClose={this.closeExportDialog}
          />
        )}

        {this.state.isProcessing && (
          <LoadingDialogComponent
            titleId={null}           // hide the dialog title
            bodyId="general.loading" // keep body text “Loading…”
            closeDialog={() => { /* keep dialog until export ends */ }}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ auth }) => ({
  activeCompanyId: auth.user.activeCompany.id,
  activeCompanyRole: auth.user.activeCompany.role
});

export default connect(mapStateToProps)(BillsPortfolioComponent);
